import { useRef, useState } from 'react';
import styled from 'styled-components';
import { Checkbox } from 'antd';
import { IoSearchSharp } from 'react-icons/io5';
import { FiFilter } from 'react-icons/fi';
import { useQuery } from 'react-query';
import { color, CredusText, CredusDropDown } from 'ui';

import ReportCard from './ReportCard';
import reportApi from '../../../api/report';
import ComponentLoader from '../ComponentLoader';
import { NoDataIcon } from '../../../assets/images/svgs';

export interface TestReportOverView {
  testId: number;
  testname: string;
  subjectId: number;
  subjectName: string;
  periodId: number;
  categoryId: number;
  description: string;
  date: string;
  testDate: string;
  testTime: string;
}

const ReportData = () => {
  const [showFilter, setShowFilter] = useState(false);
  const [sortFilter, setSortFilter] = useState(false);
  const [institutionId] = useState(localStorage.getItem('c-iid') || '');
  const [search, setSearch] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const [pageSize] = useState(100);
  const [pageNumber] = useState(1);
  const [dropDownOption, setDropDownOption] = useState('Oldest - Newest');
  const [overviewSort, setOverviewSort] = useState<
    'Oldest_Newest' | 'Newest_Oldest'
  >('Oldest_Newest');
  const { data, isLoading } = useQuery(
    ['getOverview', institutionId, overviewSort, search, pageSize, pageNumber],
    () =>
      reportApi.getOverview({
        institutionId: Number(institutionId),
        isDashboard: false,
        pageNumber,
        pageSize,
        overviewSort,
        search,
      })
  );  

  function handleReportSearch(e: React.ChangeEvent<HTMLInputElement>) {
    setSearchTerm(e.target.value);
    const value = e.target.value;
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      setSearch(value);
    }, 1000);
  }

  const filterList = ["Oldest - Newest", "Newest - Oldest"];

  return (
    <>
      {isLoading ? (
        <ComponentLoader loaderText="getting test report(s)" />
      ) : data?.tests && data?.data?.tests?.length !== 0 ? (
        <Container>
          <TopDiv>
            <SearchDiv>
              <Input
                placeholder="Try searching for information"
                value={searchTerm}
                onChange={handleReportSearch}
              />
              <IoSearchSharp color="#C3CAD9" />
            </SearchDiv>
            <FilterDiv>
              <CredusText weight="medium" size="xs">
                Filter by
              </CredusText>
              <Filter>
                <Div onClick={() => setShowFilter((prev) => !prev)}>
                  <CredusText weight="medium" size="xs">
                    Foundational
                  </CredusText>
                  <FiFilter color={'#E0DEF7'} />
                </Div>
                {showFilter && (
                  <CredusDropDown
                    items={[
                      <Item>
                        <Checkbox />{' '}
                        <CredusText size="xs">Foundational</CredusText>
                      </Item>,
                    ]}
                  />
                )}
              </Filter>
              <Filter>
                <Div onClick={() => setSortFilter((prev) => !prev)}>
                  <CredusText weight="medium" size="xs">
                   {dropDownOption}
                  </CredusText>
                  <FiFilter color={'#E0DEF7'} />
                </Div>
                {sortFilter && (
                  <CredusDropDown
                    items={
                      filterList.map((option, index: number) => (
                        <Item key={index} onClick={() => setDropDownOption(option)}>
                          <CredusText weight="medium" size="xs">
                            {option}
                          </CredusText>
                        </Item>
                      ))
                    }
                  />
                )}
              </Filter>
            </FilterDiv>
          </TopDiv>
          <BottomDiv>
            {data.tests.map((test: TestReportOverView) => (
              <ReportCard key={data.testId} test={test} />
            ))}
          </BottomDiv>
        </Container>
      ) : (
        <NoDataDiv>
          <NoDataIcon />
          <CredusText size="lg" weight="regular">
            No Test Report is available
          </CredusText>
        </NoDataDiv>
      )}
    </>
  );
};

export default ReportData;

const Container = styled.div`
  width: 100%;
`;
const TopDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 2rem;
`;
const SearchDiv = styled.div`
  width: 326px;
  height: 36px;
  background: ${color.white};
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem;
  box-shadow: 0px 1.44px 3.6px rgba(38, 51, 77, 0.03);
`;

const Input = styled.input`
  outline: none;
  border: none;
  width: 90%;
  &::placeholder {
    color: #adb8cc;
  }
`;

const FilterDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const Filter = styled.div`
  position: relative;
  min-width: 160px;
  cursor: pointer;
`;

const Item = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const Div = styled.div`
  width: 100%;
  height: 36px;
  background: ${color.white};
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0 1rem;
  box-shadow: 0px 1.44px 3.6px rgba(38, 51, 77, 0.03);
`;

const BottomDiv = styled.div`
  width: 100%;
  display: grid;
  grid-gap: 1.2rem;
  grid-template-columns: repeat(3, 1fr);
`;

const NoDataDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding-top: 3rem;
`;
